import { Background, Controls, ReactFlow, type Connection } from '@xyflow/react'
import { Palette } from './editor/palette'
import { useCallback } from 'react'
import { NoopNode } from './editor/nodes/noop_node'
import { ResistanceNode } from './editor/nodes/resistance_node'
import { ResistanceFourWireNode } from './editor/nodes/resistance_four_wire_node'
import { HipotNode } from './editor/nodes/hipot_node'
import type { ItemTypeDetailFragment } from '@/gql/graphql'
import { LoadNode } from './editor/nodes/load_node'
import { RcdNode } from './editor/nodes/rcd_node'
import { DiodeNode } from './editor/nodes/diode_node'
import { TwistedPairNode } from './editor/nodes/twisted_pair_node'
import { CapacitanceNode } from './editor/nodes/capacitance_node'
import { LinkNode } from './editor/nodes/link_node'
import { WireNode } from './editor/nodes/wire_node'
import { WireFourWireNode } from './editor/nodes/wire_four_wire_node'
import { StepActionsContext, useTestDefinition } from '@/helpers/useTestDefinition'
import { BounceLoader } from 'react-spinners'
import { Text } from '../base/text'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'

interface Props {
  id: string
  connectorFamilies: ItemTypeDetailFragment['connectorFamilies']
  testSteps: ItemTypeDetailFragment['testSteps']
}

const translateSteppable = (steppable: ItemTypeDetailFragment['testSteps'][0]['steppable']) => {
  switch (steppable.__typename) {
    case 'CapacitanceTestStepType':
    case 'DiodeTestStepType':
    case 'LinkTestStepType':
    case 'ResistanceFourWireTestStepType':
    case 'ResistanceTestStepType':
    case 'TwistedPairTestStepType':
    case 'WireFourWireTestStepType':
    case 'WireTestStepType':
      return {
        connectorFamilyPinAId: steppable.connectorFamilyPinA.id,
        connectorFamilyPinBId: steppable.connectorFamilyPinB.id,
      }
    case 'RcdTestStepType':
      return {
        breakerCount: steppable.breakerCount,
        tripCurrent: steppable.tripCurrent,
        phaseAngle: steppable.phaseAngle,
      }
    case 'HipotTestStepType':
      return {
        voltage: steppable.voltage,
        connectorFamilyPinId: steppable.connectorFamilyPin.id,
      }
    case 'NoopTestStepType':
      return {
        result: steppable.result,
      }
    case 'LoadTestStepType':
      return {}
  }
}

export const Editor = ({ id, connectorFamilies, testSteps }: Props) => {
  const initialNodes = testSteps.map(testStep => {
    const steppable = translateSteppable(testStep.steppable)
    return { id: testStep.id, type: testStep.steppable.__typename, data: { name: testStep.name, connectorFamilies, itemTypeId: id, steppable }, position: { x: testStep.positionX, y: testStep.positionY } }
  })

  const initialEdges = testSteps.flatMap(testStep => {
    return testStep.testStepDependencies.map(dep => {
      return { id: dep.id, source: testStep.id, target: dep.dependency.id }
    })
  })

  const { nodes, edges, loading, saveSuccess, onNodesChange, onEdgesChange, changeStep, addEdge, mutationError, fieldErrors } = useTestDefinition(initialNodes, initialEdges)

  const onConnect = useCallback((params: Connection) => addEdge(params.source, params.target), [addEdge])

  const nodeTypes = {
    NoopTestStepType: NoopNode,
    LinkTestStepType: LinkNode,
    WireTestStepType: WireNode,
    WireFourWireTestStepType: WireFourWireNode,
    ResistanceTestStepType: ResistanceNode,
    ResistanceFourWireTestStepType: ResistanceFourWireNode,
    HipotTestStepType: HipotNode,
    CapacitanceTestStepType: CapacitanceNode,
    TwistedPairTestStepType: TwistedPairNode,
    DiodeTestStepType: DiodeNode,
    RcdTestStepType: RcdNode,
    LoadTestStepType: LoadNode,
  }

  return (
    <div className="flex flex-col md:flex-row grow h-full">
      <div className="grow border border-zinc-700 rounded-xl p-1">
        <StepActionsContext.Provider value={{ changeStep }}>
          <ReactFlow
            colorMode="system"
            edges={ edges }
            nodes={ nodes }
            nodeTypes={ nodeTypes }
            snapGrid={ [25, 25] }
            fitView
            snapToGrid
            onConnect={ onConnect }
            onEdgesChange={ onEdgesChange }
            onNodesChange={ onNodesChange }
          >
            <Controls />
            <Background />
          </ReactFlow>
        </StepActionsContext.Provider>
      </div>
      <div className="flex flex-col pl-3 justify-between">
        <Palette defaultData={{ connectorFamilies }} itemTypeId={ id } />
        <div className="flex flex-col justify-end items-center gap-3">
          { loading && <>
            <Text>Saving</Text>
            <BounceLoader color="lightgray" size={ 16 } />
          </> }
          { mutationError && <Text className="max-w-62.5" color="text-red-500">{ mutationError }</Text> }
          { Object.keys(fieldErrors).length > 0 && <Text className="max-w-62.5" color="text-red-500">{ JSON.stringify(fieldErrors) }</Text> }
          { saveSuccess === true && <CheckIcon className="inset-0 transition duration-150 text-green-600 h-8 w-8" /> }
          { saveSuccess === false && <XMarkIcon className="inset-0 transition duration-150 text-red-600 h-8 w-8" /> }
        </div>
      </div>
    </div>
  )
}
